import React, {useCallback, useEffect, useState} from 'react';

import {
  useMobileBreakpoint,
  Box,
  Typography,
  EnterIcon,
  Divider,
  ListItem
} from '@customink/pigment-react';
import {useAuth} from '~/auth/AuthContext';
import {useDispatch} from 'react-redux';
import {updateLabExitWarningEligibility} from '~/actions/ConfigActions';
import {DesignUnsavedDialog} from './DesignUnsavedDialog';
import {useNavigate} from 'react-router-dom';
import RemoteServiceURIs from '~/constants/RemoteServices';
import {pushGtmEvent} from '~/utils/analytics';
import {getCheckoutSummaryLink} from '../../constants/CheckoutLink';

enum DismissType {
  // eslint-disable-next-line no-unused-vars
  ACCOUNT = 'account',
  // eslint-disable-next-line no-unused-vars
  LOGOUT = 'sign out',
  // eslint-disable-next-line no-unused-vars
  LOGO = 'logo',
  // eslint-disable-next-line no-unused-vars
  CART = 'cart'
}

type AccountsMenuProps = {
  isDesignDirty: boolean;
};

const AccountsMenu = ({isDesignDirty}: AccountsMenuProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [dismiss, setDismiss] = useState<DismissType | null>(null);
  const navigate = useNavigate();
  const {logout} = useAuth();
  const dispatch = useDispatch();
  const isMobile = useMobileBreakpoint();
  const handleRedirectToDesigns = useCallback(() => {
    pushGtmEvent({
      event: 'meganav',
      click_section: 'my account',
      click_link: 'Go to My Account'
    });
    window.location.href = `${RemoteServiceURIs.accounts}/account/designs`;
  }, []);
  const showDialog = useCallback(
    (dismissType: DismissType) => {
      if (isMobile) {
        (
          document
            .querySelector('ci-accounts')
            ?.shadowRoot?.querySelector('ci-dialog') as any
        )?.forceClose();
        (
          document
            .querySelector('ci-cart')
            ?.shadowRoot?.querySelector('ci-dialog') as any
        )?.forceClose();
      }
      dispatch(updateLabExitWarningEligibility(false));
      setDialogOpen(true);
      setDismiss(dismissType);
      pushGtmEvent({
        event: 'lab_interaction',
        interaction_category: 'save design alert view',
        interaction_action: dismissType
      });
    },
    [isMobile, dispatch, setDialogOpen, setDismiss]
  );
  const handleRedirectToCart = useCallback(() => {
    window.location.href = getCheckoutSummaryLink();
  }, []);
  const onRedirectToCart = useCallback(
    (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isDesignDirty) {
        setSubtitle(
          'You will lose any unsaved changes by continuing to your cart.'
        );
        return showDialog(DismissType.CART);
      }
      handleRedirectToCart();
    },
    [isDesignDirty, showDialog, handleRedirectToCart]
  );
  const handleLogoRedirect = useCallback(() => {
    window.location.href = RemoteServiceURIs.accounts;
  }, []);
  const handleTrackingSignup = useCallback(
    () => pushGtmEvent({event: 'lab_signup'}),
    []
  );
  const handleTrackingSignIn = useCallback(
    () => pushGtmEvent({event: 'lab_signin'}),
    []
  );
  const onLogoRedirect = useCallback(
    (event: Event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      if (isDesignDirty) {
        setSubtitle(
          'You will lose any unsaved changes by continuing to the homepage.'
        );
        return showDialog(DismissType.LOGO);
      }
      handleLogoRedirect();
    },
    [isDesignDirty, showDialog]
  );
  const onRedirectToDesigns = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      event.preventDefault();
      if (isDesignDirty) {
        setSubtitle(
          'You will lose any unsaved changes by continuing to your account.'
        );
        return showDialog(DismissType.ACCOUNT);
      }
      handleRedirectToDesigns();
    },
    [isDesignDirty, showDialog, handleRedirectToDesigns]
  );

  const handleClose = useCallback(() => {
    dispatch(updateLabExitWarningEligibility(true));
    setDialogOpen(false);
  }, []);
  const handleCloseDialog = useCallback(() => {
    pushGtmEvent({
      event: 'lab_interaction',
      interaction_category: 'save design alert click',
      interaction_action: 'close'
    });
    handleClose();
  }, []);
  const handleSubmit = useCallback(() => {
    handleClose();
    navigate('/next/saveForm?src=cartSavePrompt');
    pushGtmEvent({
      event: 'lab_interaction',
      interaction_category: 'save design alert click',
      interaction_action: 'save my design'
    });
  }, []);
  const handleLogout = useCallback(() => {
    pushGtmEvent({
      event: 'meganav',
      click_section: 'my account',
      click_link: 'sign out'
    });
    logout();
  }, []);
  const onLogout = useCallback(
    (event: React.MouseEvent) => {
      if (isDesignDirty) {
        setSubtitle('You will lose any unsaved changes by signing out.');
        return showDialog(DismissType.LOGOUT);
      }
      handleLogout();
    },
    [isDesignDirty, showDialog, handleLogout]
  );

  const handleDismiss = useCallback(() => {
    setDialogOpen(false);
    if (dismiss === DismissType.ACCOUNT) {
      handleRedirectToDesigns();
    } else if (dismiss === DismissType.LOGOUT) {
      handleLogout();
    } else if (dismiss === DismissType.LOGO) {
      handleLogoRedirect();
    } else if (dismiss === DismissType.CART) {
      handleRedirectToCart();
    }
    pushGtmEvent({
      event: 'lab_interaction',
      interaction_category: 'save design alert click',
      interaction_action: 'continue without saving'
    });
  }, [
    dismiss,
    handleRedirectToDesigns,
    handleLogout,
    handleLogoRedirect,
    handleRedirectToCart
  ]);
  const attachEventListeners = useCallback(() => {
    const logoElement = document.querySelector('.ciHeader-logo');
    const cartElement = document.querySelector('ci-cart');
    const signInElement = document.querySelector('[data-testid="my-account"]');
    const ciAccountsElement = document.querySelector('ci-accounts');

    if (logoElement) {
      logoElement.addEventListener('click', onLogoRedirect, true);
    }
    if (signInElement) {
      signInElement.addEventListener('click', handleTrackingSignIn, true);
    }
    if (ciAccountsElement && ciAccountsElement.shadowRoot) {
      const signInElement = ciAccountsElement.shadowRoot.querySelector(
        '[data-testid="my-account-login"]'
      );
      if (signInElement) {
        signInElement.addEventListener('click', handleTrackingSignIn);
      }
      const signUpElement = ciAccountsElement.shadowRoot.querySelector(
        '[data-testid="my-account-signup"]'
      );
      if (signUpElement) {
        signUpElement.addEventListener('click', handleTrackingSignup);
      }
    }

    if (cartElement && cartElement.shadowRoot) {
      const cartLink = cartElement.shadowRoot.querySelector('a');
      const cartButton = cartElement.shadowRoot.querySelector('button');

      if (cartLink) {
        cartLink.addEventListener('click', onRedirectToCart, true);
      }

      if (cartButton) {
        cartButton.addEventListener('click', onRedirectToCart, true);
      }
    }
  }, [
    onLogoRedirect,
    onRedirectToCart,
    handleTrackingSignIn,
    handleTrackingSignup
  ]);

  useEffect(() => {
    attachEventListeners();
    const observer = new MutationObserver(() => {
      attachEventListeners();
    });

    observer.observe(document.body, {childList: true, subtree: true});

    return () => {
      const logoElement = document.querySelector('.ciHeader-logo');
      const cartElement = document.querySelector('ci-cart');
      const signInElement = document.querySelector(
        '[data-testid="my-account"]'
      );
      const ciAccountsElement = document.querySelector('ci-accounts');

      if (logoElement) {
        logoElement.removeEventListener('click', onLogoRedirect, true);
      }
      if (signInElement) {
        signInElement.removeEventListener('click', handleTrackingSignIn);
      }
      if (ciAccountsElement && ciAccountsElement.shadowRoot) {
        const signInElement = ciAccountsElement.shadowRoot.querySelector(
          '[data-testid="my-account-login"]'
        );
        if (signInElement) {
          signInElement.removeEventListener('click', handleTrackingSignIn);
        }
        const signUpElement = ciAccountsElement.shadowRoot.querySelector(
          '[data-testid="my-account-signup"]'
        );
        if (signUpElement) {
          signUpElement.removeEventListener('click', handleTrackingSignup);
        }
      }

      if (cartElement && cartElement.shadowRoot) {
        const cartLink = cartElement.shadowRoot.querySelector('a');
        const cartButton = cartElement.shadowRoot.querySelector('button');

        if (cartLink) {
          cartLink.removeEventListener('click', onRedirectToCart, true);
        }

        if (cartButton) {
          cartButton.removeEventListener('click', onRedirectToCart, true);
        }
      }

      observer.disconnect();
    };
  }, [onLogoRedirect, onRedirectToCart]);
  return (
    <>
      <Box>
        <ListItem
          onClick={onRedirectToDesigns}
          sx={isMobile ? {paddingX: '16px'} : {}}>
          <Typography variant="uiTextDefault">Go to My Account</Typography>
        </ListItem>
        <Divider sx={{marginY: '8px'}} />
        <ListItem
          data-testid="sign-out"
          onClick={onLogout}
          sx={isMobile ? {paddingX: '16px'} : {}}
          startIcon={<EnterIcon fontSize="small" />}>
          <Typography variant="uiTextDefault">Sign Out</Typography>
        </ListItem>
      </Box>
      <DesignUnsavedDialog
        open={dialogOpen}
        subtitle={subtitle}
        onSubmit={handleSubmit}
        onDismiss={handleDismiss}
        onClose={handleCloseDialog}
      />
    </>
  );
};

export default AccountsMenu;
